import React, { useState, useCallback } from "react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { useHotel } from "../../contexts/HotelContext";
import Layout from "../../components/Layout";
import { useLlmUsageDashboard } from "../../hooks/useLlmUsageDashboard";
import { useLlmUsageExport } from "../../hooks/useLlmUsageExport";
import {
  SummaryCards,
  DateRangePicker,
  DailyTrendChart,
  BreakdownCharts,
  TopRankings,
  ExportButton,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  AccessDeniedState,
} from "../../components/admin/llm-usage";

const getDefaultDateRange = () => {
  const now = new Date();
  const start = startOfDay(subDays(now, 30));
  const end = endOfDay(now);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
};

const LlmUsage: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();

  // Date range state
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Query hook
  const {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  } = useLlmUsageDashboard({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  // Export hook
  const {
    exportCsv,
    isLoading: isExporting,
    isRateLimited,
    remainingExports,
    countdown,
  } = useLlmUsageExport();

  // Check access — mirror ModeratorRoute: global admin OR kiedynabory
  // moderator module. Tenant-level grants:schedule is enforced by the
  // backend @RequiredModule('grants:schedule') ModuleGuard.
  const canModerate =
    user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");

  // Handle date change
  const handleDateChange = useCallback((startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
  }, []);

  // Handle export
  const handleExport = useCallback(() => {
    exportCsv({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  }, [exportCsv, dateRange]);

  // Redirect users without moderator access
  if (!canModerate) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Panel użycia AI
            </h1>
          </div>
          <AccessDeniedState message="Ta strona jest dostępna tylko dla administratorów z dostępem do modułu grants:schedule." />
        </div>
      </Layout>
    );
  }

  // Determine if data is empty
  const isEmpty = data && data.summary.recordCount === 0;

  return (
    <Layout>
      <div className="relative space-y-6">
        {/* Refetch Overlay */}
        {isRefetching && !isLoading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Panel użycia AI
            </h1>
            <p className="text-neutral-600 mt-1">
              Monitoruj koszty i użycie AI w procesach przetwarzania KiedyNabory.pl
            </p>
          </div>

          <ExportButton
            onClick={handleExport}
            isLoading={isExporting}
            isRateLimited={isRateLimited}
            remainingExports={remainingExports}
            countdown={countdown}
            disabled={isLoading || !!error || isEmpty}
          />
        </div>

        {/* Date Range Picker */}
        <div className="bg-white rounded-lg border border-neutral-200 p-4">
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onDateChange={handleDateChange}
            maxDays={365}
          />
          {data && (
            <div className="mt-2 text-sm text-neutral-500">
              Dane od{" "}
              <span className="font-medium">
                {format(new Date(data.dateRange.startDate), "d MMM yyyy")}
              </span>{" "}
              do{" "}
              <span className="font-medium">
                {format(new Date(data.dateRange.endDate), "d MMM yyyy")}
              </span>
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading && <LoadingSkeleton />}

        {/* Error State */}
        {error && <ErrorState error={error} onRetry={() => refetch()} />}

        {/* Empty State */}
        {!isLoading && !error && isEmpty && <EmptyState />}

        {/* Dashboard Content */}
        {!isLoading && !error && data && !isEmpty && (
          <>
            {/* Summary Cards */}
            <SummaryCards summary={data.summary} />

            {/* Daily Trend Chart */}
            <DailyTrendChart data={data.byDay} />

            {/* Breakdown Charts */}
            <BreakdownCharts
              byBoundedContext={data.byBoundedContext}
              byOperation={data.byOperation}
              byModel={data.byModel}
            />

            {/* Top Rankings */}
            <TopRankings
              topSources={data.topSources}
              topProjects={data.topProjects}
              topOperators={data.topOperators}
              topArticles={data.topArticles}
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default LlmUsage;
